import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import type { Playlist, PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useUIStore } from '@jwp/ott-common/src/stores/UIStore';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import { createURL, liveChannelsURL } from '@jwp/ott-common/src/utils/urlFormatting';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import useLiveChannels from '@jwp/ott-hooks-react/src/useLiveChannels';
import usePlaylistLiveChannelsParams from '@jwp/ott-ui-react/src/hooks/usePlaylistLiveChannelsParams';
import useEpgPlaybackUrl from '@jwp/ott-ui-react/src/hooks/useEpgPlaybackUrl';
import useLiveProgram from '@jwp/ott-hooks-react/src/useLiveProgram';
import type { EpgProgram } from '@jwp/ott-common/types/epg';
import Cinema from '@jwp/ott-ui-react/src/containers/Cinema/Cinema';
import env from '@jwp/ott-common/src/env';
import { getModule } from '@jwp/ott-common/src/modules/container';
import EpgController from '@jwp/ott-common/src/controllers/EpgController';
import { programIsLive, programIsUpcoming, programIsVod, getDayOptions } from '@jwp/ott-common/src/utils/epg';
import useInfiniteLiveChannels from '@jwp/ott-hooks-react/src/useInfiniteLiveChannels';
import { createLiveEventMetadata } from '@jwp/ott-common/src/utils/metadata';
import { MediaStatus } from '@jwp/ott-common/src/utils/liveEvent';

import type { ScreenComponent } from '../../../../../types/screens';
import EpgHeader from '../../../../components/Epg/EpgHeader';
import EpgSchedule from '../../../../components/Epg/schedule/EpgSchedule';
import EpgLiveNow from '../../../../components/Epg/EpgLiveNow';
import EpgVerticalGuide from '../../../../components/Epg/vertical/EpgVerticalGuide';
import Loading from '../../../Loading/Loading';
import EventTags from '../../../../components/EventTags/EventTags';

const PlaylistLiveChannels: ScreenComponent<Playlist> = ({ data: { feedid, playlist, title, description } }) => {
  const { i18n } = useTranslation('epg');
  const breakpoint = useBreakpoint();
  const navigate = useNavigate();
  const epgController = getModule(EpgController);
  const { channelId, programId, view, dayOffset, play, liveStartDateTime, liveEndDateTime, liveFromBeginning } = usePlaylistLiveChannelsParams();
  const epgDays = epgController.getEpgDays(playlist);
  const dayOptions = useMemo(() => getDayOptions(epgDays, epgDays), [epgDays]);

  const { config } = useConfigStore(({ config }) => ({ config }), shallow);
  const { siteName } = config;

  const { channels, hasPreviousPage, hasNextPage, fetchPreviousPage, fetchNextPage, loadedDays, isFetching } = useInfiniteLiveChannels({
    playlist,
    maxPastDays: epgDays,
    maxFutureDays: epgDays,
    dayOffset,
  });

  const getPlaybackUrl = useEpgPlaybackUrl(playlist, channels);

  const {
    program: currentProgram,
    upcomingProgram,
    channel,
  } = useLiveChannels({
    channels,
    channelId,
    programId,
    enableAutoUpdate: !liveFromBeginning,
  });
  const program = currentProgram || upcomingProgram;
  const pageTitle = `${title} - ${siteName}`;
  const isMobile = breakpoint <= Breakpoint.sm;

  // Legacy code to make player work
  const { isLive, isVod, isWatchableFromBeginning } = useLiveProgram({ program, catchupHours: channel?.catchupHours });

  const goBack = () =>
    feedid &&
    navigate(
      createURL(liveChannelsURL(feedid, channelId, false, title), {
        program: program && !programIsLive(program) ? program.id : undefined,
        view,
        offset: dayOffset || undefined,
      }),
    );
  const channelMediaItem = useMemo(() => playlist.find(({ mediaid }) => channel?.id === mediaid), [channel?.id, playlist]);
  const { isEntitled } = useEntitlement(channelMediaItem);

  const videoDetails = useMemo(() => {
    if (program) {
      return {
        title: program.title,
        description: program.description || '',
        image: program.backgroundImage,
        canWatch: isLive || (isVod && isWatchableFromBeginning),
        canWatchFromBeginning: isEntitled && isLive && isWatchableFromBeginning,
      };
    }

    return {
      title: channel?.title || '',
      description: channel?.description || '',
      image: channel?.backgroundImage,
      canWatch: true,
      canWatchFromBeginning: false,
    };
  }, [channel?.backgroundImage, channel?.description, channel?.title, isEntitled, isLive, isVod, isWatchableFromBeginning, program]);

  const playingItem = useMemo(() => {
    // need to use these here to ensure this only reflects the state of the selected program
    const currentProgramIsLive = !!program && programIsLive(program);
    const currentProgramIsVod = !!program && programIsVod(program);
    const isLiveFromBeginning = currentProgramIsLive && liveFromBeginning;

    if (program && channelMediaItem && channel && (currentProgramIsVod || isLiveFromBeginning)) {
      // only play the program as its own media item when it is backend resolvable
      const navigation = epgController.getProgramNavigation(channelMediaItem, program, channel);

      if (navigation?.programId) {
        return {
          ...channelMediaItem,
          mediaid: navigation.programId,
          title: program.title,
          description: program.description,
        } as PlaylistItem;
      }
    }

    // live channel
    return channelMediaItem;
  }, [program, liveFromBeginning, channelMediaItem, channel, epgController]);

  const primaryMetadata = useMemo(() => {
    if (!channel) {
      return '';
    }

    if (!program) {
      return <EventTags mediaStatus={MediaStatus.LIVE} attributes={[]} channel={channel} />;
    }

    const startTime = new Date(program.startTime);
    const endTime = new Date(program.endTime);
    const liveEventMetadata = program ? createLiveEventMetadata(startTime, endTime, i18n.language) : [];
    const isLive = programIsLive(program);
    const isUpcoming = programIsUpcoming(program);
    const mediaStatus = isLive && !liveFromBeginning ? MediaStatus.LIVE : isUpcoming ? MediaStatus.SCHEDULED : MediaStatus.VOD;

    return <EventTags mediaStatus={mediaStatus} attributes={liveEventMetadata} channel={channel} />;
  }, [channel, i18n.language, program, liveFromBeginning]);

  const handleProgramClick = (program: EpgProgram, fromBeginning: boolean = false) => {
    const programChannel = channels.find(({ id }) => id === program.channelId);

    if (!programChannel) return;

    const playbackUrl = getPlaybackUrl(programChannel.id, program, fromBeginning, view, dayOffset);

    if (!playbackUrl) return;

    navigate(playbackUrl);
  };

  const getProgramUrl = (channelId: string, programId: string) => {
    const channel = channels.find((channel) => channel.id === channelId);
    const program = channel?.programs.find((program) => program.id === programId);
    if (channel && program) return getPlaybackUrl(channel.id, program, false, view, dayOffset);
  };

  // Effects
  useEffect(() => {
    // update the channel id in URL
    if (channel && feedid && channelId !== channel.id) {
      navigate(liveChannelsURL(feedid, channel.id, false, title), { replace: true });
    }
  }, [navigate, feedid, channel, channelId, title]);

  useEffect(() => {
    useUIStore.setState({ hideFooter: true });
    return () => useUIStore.setState({ hideFooter: false });
  }, []);

  // Loading (channel and feedid must be defined)
  if (!feedid) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={`${env.APP_PUBLIC_URL}${liveChannelsURL(feedid, undefined, false, title)}`} />
        {description ? <meta name="description" content={description} /> : null}
      </Helmet>
      <EpgHeader
        title={title}
        dayOptions={dayOptions}
        day={dayOffset}
        view={view}
        onViewChange={(view) => {
          navigate(createURL(liveChannelsURL(feedid, channel.id, false, title), { view, offset: undefined }), { replace: true });
        }}
        onDayChange={(offset) => {
          navigate(createURL(liveChannelsURL(feedid, channel.id, false, title), { view, offset: offset || undefined }), { replace: true });
        }}
      />
      {view === 'guide' &&
        (isMobile ? (
          <EpgVerticalGuide
            onProgramClick={handleProgramClick}
            setChannel={(channel) =>
              navigate(createURL(liveChannelsURL(feedid, undefined, false, title), { channel, program: undefined, view, offset: dayOffset || undefined }))
            }
            channel={channel}
            channels={channels}
            isFetching={isFetching}
            selectedDayOffset={dayOffset}
          />
        ) : (
          <EpgSchedule
            channels={channels}
            onProgramClick={handleProgramClick}
            isFetching={isFetching}
            selectedDayOffset={dayOffset}
            hasPreviousPage={hasPreviousPage}
            hasNextPage={hasNextPage}
            fetchPreviousPage={fetchPreviousPage}
            fetchNextPage={fetchNextPage}
            loadedDays={loadedDays}
          />
        ))}
      {view === 'live' && <EpgLiveNow channels={channels} getProgramUrl={getProgramUrl} isFetching={isFetching} />}
      {playingItem && (
        <Cinema
          open={play && isEntitled}
          onClose={goBack}
          item={playingItem}
          contextualActions={videoDetails.canWatchFromBeginning && !liveFromBeginning ? ['START_FROM_BEGINNING'] : []}
          onContextualActionClick={(action) => {
            if (action === 'START_FROM_BEGINNING') {
              navigate(createURL(liveChannelsURL(feedid, channel.id, true, title), { beginning: 1 }));
            }
          }}
          title={videoDetails.title}
          primaryMetadata={primaryMetadata}
          feedId={feedid}
          liveStartDateTime={liveStartDateTime}
          liveEndDateTime={liveEndDateTime}
          liveFromBeginning={liveFromBeginning}
        />
      )}
    </>
  );
};

export default PlaylistLiveChannels;
